import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/sheet";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";
import { Copy, Check, Building2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";

interface TenantDetailsSheetProps {
  tenant: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TenantDetailsSheet: React.FC<TenantDetailsSheetProps> = ({ tenant, open, onOpenChange }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!tenant) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto user-detail-view">
        <SheetHeader className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Building2 className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-bold">Organization Details</SheetTitle>
                <p className="text-sm mt-1">Detailed registration information of the organization</p>
              </div>
            </div>
          </div>
        </SheetHeader>

        <div className="p-6 space-y-6">
          <TooltipProvider>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Organization Code</p>
              <div className="flex items-center space-x-2">
                <p className="text-sm font-mono truncate bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 p-2.5 rounded flex-1">
                  {tenant.code || "—"}
                </p>
                {tenant.code && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10"
                        onClick={() => copyToClipboard(tenant.code)}
                      >
                        {copied ? <Check className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />}
                        Copy
                      </Button>
                    </TooltipTrigger>
                  </Tooltip>
                )}
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Organization Name</p>
                <p className="text-md font-medium">{tenant.name || "—"}</p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Company Type</p>
                <p className="text-md font-medium">{tenant.companyType || "—"}</p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Industry</p>
                <p className="text-md font-medium">{tenant.industry || "—"}</p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Country</p>
                <p className="text-md font-medium">{tenant.country || "—"}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Street Address</p>
                <p className="text-md font-medium">{tenant.address || "—"}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">City</p>
                  <p className="text-md font-medium">{tenant.city || "—"}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">State</p>
                  <p className="text-md font-medium">{tenant.state || "—"}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Postal Code</p>
                  <p className="text-md font-medium">{tenant.postalCode || "—"}</p>
                </div>
              </div>
            </div>
          </TooltipProvider>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TenantDetailsSheet;
